import type { AdminUserSummary, KycCase, KycEscalation, KycEscalationKind, KycEscalationStatus } from "../types";
import { kycStage } from "./kycOps";

/**
 * A declined or frozen customer cannot be retried, and no control in this portal can
 * overturn the decision. The only path that moves one is a compliance ticket with the
 * banking provider, which is a different thing from the internal case: the internal
 * case tracks *our* work, the escalation tracks *theirs*.
 *
 * Provider guidance, 2026-09-01: compliance issues go to Zendesk first; Slack is for
 * operational problems and for warning about an imminent breach, not for routine
 * priority requests. The queue is shared across partners and asking for priority by
 * default removes its fairness. The compliance SLA is two calendar days, weekends
 * included.
 */
export const ESCALATION_SLA_DAYS = 2;
const DAY_MS = 86_400_000;

/** How long a provider-held case may sit before it is worth a ticket of its own. */
export const STALLED_REVIEW_HOURS = 48;

interface KindInfo {
  label: string;
  /**
   * Kept word-for-word in step with the gateway's own `ESCALATION_ASK`, because this
   * copy renders the pre-send preview and the gateway composes what is actually filed.
   */
  ask: string;
  /** Set honest expectations before an operator spends the provider's queue time. */
  likelihood: string;
}

const KIND_INFO: Readonly<Record<KycEscalationKind, KindInfo>> = {
  reinstate: {
    label: "Reset a declined customer",
    ask: "Please reset this customer from rejected to unverified so they can attempt verification again.",
    likelihood: "Rejections come from the provider's automated checks and are the hardest to overturn. Make the case for why this customer should get another attempt; a reset is never guaranteed."
  },
  unfreeze: {
    label: "Unfreeze a customer",
    ask: "Please review this frozen customer and unfreeze them if the underlying issue is resolved.",
    likelihood: "Reversible case by case. Freezes often follow returns, and partners that can show a collection process do get customers unfrozen."
  },
  stalled_review: {
    label: "Chase a stalled review",
    ask: "Please confirm the status of this identity verification, which has been under review past the two-day compliance SLA.",
    likelihood: "Expect an answer within the two calendar day SLA. Escalate in Slack only if that window is about to breach."
  },
  duplicate: {
    label: "Confirm the canonical customer",
    ask: "Please confirm which customer GUID is canonical for this individual so we can retire the duplicate.",
    likelihood: "Customers cannot be deleted — one that has progressed through KYC or transacted is a legal entity. The remedy is to revert to the original customer GUID for all future activity."
  }
};

export function escalationKindInfo(kind: KycEscalationKind): KindInfo {
  return KIND_INFO[kind];
}

/**
 * The escalation this state calls for, or null when the provider is not the blocker.
 * A duplicate is never inferred — an operator asserts it, because nothing in the
 * customer record marks one.
 */
export function suggestedEscalationKind(state: string, ageDays: number | null): KycEscalationKind | null {
  const normalized = state.trim().toLowerCase();
  if (normalized === "rejected") return "reinstate";
  if (normalized === "frozen") return "unfreeze";
  if (kycStage(normalized) === "processing" && ageDays !== null && ageDays >= STALLED_REVIEW_HOURS / 24) return "stalled_review";
  return null;
}

/**
 * Mirrors the gateway's own rule. The server re-derives this and rejects anything it
 * disagrees with; this copy only decides whether the button is offered.
 *
 * Any unverified customer qualifies, because a duplicate can be spotted in any state
 * and is always operator-asserted. Narrowing this to the states with a *suggested* ask
 * would leave an operator who has found a duplicate on, say, an expired customer with
 * no way to raise it.
 */
export function canEscalate(user: AdminUserSummary, item: KycCase | undefined): boolean {
  if (kycStage(user.state) === "verified") return false;
  return !(item?.escalation && isEscalationLive(item.escalation));
}

/** An escalation the provider still owns. Resolved and declined ones are history. */
export function isEscalationLive(escalation: KycEscalation): boolean {
  return escalation.status === "awaiting_reference" || escalation.status === "open";
}

export function escalationDueAt(openedAt: string): string | null {
  const opened = Date.parse(openedAt);
  if (!Number.isFinite(opened)) return null;
  return new Date(opened + ESCALATION_SLA_DAYS * DAY_MS).toISOString();
}

export type EscalationSlaState = "unfiled" | "on_track" | "due_soon" | "breached" | "closed";

/**
 * `unfiled` is deliberately distinct from `on_track`: an escalation with no provider
 * reference has not started their clock at all, and reading it as on track is how a
 * case sits for a week while everyone assumes the provider has it.
 */
export function escalationSlaState(escalation: KycEscalation, now = Date.now()): EscalationSlaState {
  if (!isEscalationLive(escalation)) return "closed";
  if (!escalation.reference) return "unfiled";
  const due = escalation.openedAt ? escalationDueAt(escalation.openedAt) : null;
  if (!due) return "on_track";
  const remaining = Date.parse(due) - now;
  if (remaining <= 0) return "breached";
  return remaining <= DAY_MS / 2 ? "due_soon" : "on_track";
}

export function escalationSlaLabel(state: EscalationSlaState): string {
  switch (state) {
    case "unfiled": return "Not filed with the provider";
    case "on_track": return "Within the 2-day SLA";
    case "due_soon": return "SLA due within 12 hours";
    case "breached": return "Past the 2-day SLA";
    case "closed": return "Closed";
  }
}

export function escalationStatusLabel(status: KycEscalationStatus): string {
  switch (status) {
    case "awaiting_reference": return "Awaiting ticket reference";
    case "open": return "Open with provider";
    case "resolved": return "Resolved by provider";
    case "declined": return "Declined by provider";
  }
}

export interface EscalationPacketInput {
  kind: KycEscalationKind;
  environment: string;
  customerGuid: string | null;
  identityVerificationGuid: string | null;
  personaInquiryId: string | null;
  appStatus: string | null;
  ageDays: number | null;
  operatorNote: string;
  /** For a duplicate, the customer GUID we believe is canonical. */
  canonicalCustomerGuid?: string | null;
}

/**
 * The pre-send preview of what the gateway will file. Structured to match
 * `buildEscalationPacket` in the gateway line for line, because a preview that differs
 * from what is sent is worse than no preview: identifiers only, no name, email, phone,
 * or identity data. The provider indexes on the customer GUID and asks for the
 * verification and Persona references to save digging; none of that requires attaching
 * a person's identity to a support thread.
 */
export function buildEscalationPacket(input: EscalationPacketInput): { subject: string; body: string } {
  const info = escalationKindInfo(input.kind);
  const subject = `${info.label} — customer ${input.customerGuid ?? "unknown"} (${input.environment})`;
  const lines = [
    info.ask,
    "",
    "Identifiers",
    `Environment: ${value(input.environment)}`,
    `Customer GUID: ${value(input.customerGuid)}`,
    `Identity verification GUID: ${value(input.identityVerificationGuid)}`,
    `Persona inquiry ID: ${value(input.personaInquiryId)}`
  ];
  if (input.kind === "duplicate") lines.push(`Suspected canonical customer GUID: ${value(input.canonicalCustomerGuid ?? null)}`);
  lines.push(
    "",
    "Observed state",
    `Derived app status: ${value(input.appStatus)}`,
    `Days in this state: ${input.ageDays === null ? "Unknown" : String(input.ageDays)}`,
    "",
    "Context",
    input.operatorNote.trim() || "No additional context provided."
  );
  return { subject, body: lines.join("\n") };
}

function value(raw: string | null): string {
  return raw && raw.trim() ? raw.trim() : "Unavailable";
}
